import { 
  Box, Button, Divider, Paper, Stack, Typography, 
  CircularProgress, TextField, Fade 
} from '@mui/material';
import React, { useState, useMemo } from 'react';
import { PatternFormat } from 'react-number-format';
import { useAgendamentoStore } from '../../store/useAgendamentoStore';
import { AgendamentoService } from '../../services/agendamentoservice';
import { AgendamentoStep, ResumoEnvioDto } from '../../agendamento.types';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { InfoAgendamento } from '../agendamento/InfoAgendamento';

export const StepResumo: React.FC = () => {
  const { unidade, servico, data, horario, profissional, setStep } = useAgendamentoStore();
  
  const [loading, setLoading] = useState(false);
  const [contato, setContato] = useState({ nome: '', celular: '' });

  /* =====================================================
     VALIDAÇÃO DINÂMICA
  ===================================================== */
  const isValido = useMemo(() => {
    const nomeLimpo = contato.nome.trim();
    const temNomeCompleto = nomeLimpo.split(/\s+/).length >= 2 && nomeLimpo.length >= 6;

    const apenasNumeros = contato.celular.replace(/\D/g, '');
    // Aceita fixo (10) ou celular (11)
    const celularValido = apenasNumeros.length >= 10 && apenasNumeros.length <= 11;

    return temNomeCompleto && celularValido;
  }, [contato]);

  const valorTotal = (servico?.valor || 0) + (profissional?.valor || 0);

  const handleFinalizar = async () => {
    if (!isValido) return;

    setLoading(true);

    const payload: ResumoEnvioDto = {
      nome: contato.nome.trim(),
      celular: contato.celular.replace(/\D/g, ''), 
      data: data!,
      horario: horario!,
      executante: { codigo: profissional!.codigo },
      unidade: { codigo: unidade!.codigo },
      produto: { codigo: servico!.codigo }
    };

    try {
      await AgendamentoService.agendar(payload);
      setStep(AgendamentoStep.CONFIRMACAO);
    } catch (error) {
      console.error("Erro ao agendar:", error);
      alert("Ocorreu um erro ao processar seu agendamento.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={600}>
      <Box>
        <InfoAgendamento />
        
        <Typography variant="h6" fontWeight={800} mb={3}>Finalizar Agendamento</Typography>

        <Stack spacing={3}>
          {/* Dados de Contato */}
          <Paper sx={{ p: 3, borderRadius: 6, border: '1px solid rgba(0,0,0,0.08)' }}>
            <Typography variant="subtitle2" fontWeight={800} mb={2}>Seus Dados</Typography>
            <Stack spacing={2}>
              <TextField 
                fullWidth 
                label="Nome completo" 
                variant="outlined"
                value={contato.nome}
                onChange={(e) => setContato({...contato, nome: e.target.value})}
                error={contato.nome.length > 5 && contato.nome.trim().split(/\s+/).length < 2}
                helperText={contato.nome.length > 5 && contato.nome.trim().split(/\s+/).length < 2 ? "Informe nome e sobrenome" : ""}
              />

              {/* Input com Máscara Brasileira */}
              <PatternFormat
                format="(##) #####-####"
                mask="_"
                customInput={TextField}
                fullWidth
                label="WhatsApp / Celular"
                variant="outlined"
                value={contato.celular}
                onValueChange={(values) => {
                  setContato({...contato, celular: values.value});
                }}
                error={contato.celular.length > 0 && contato.celular.length < 10}
                helperText={contato.celular.length > 0 && contato.celular.length < 10 ? "Número incompleto" : ""}
              />
            </Stack>
          </Paper>

          {/* Card de Valores */}
          <Paper sx={{ p: 3, borderRadius: 6, border: '1px solid rgba(0,0,0,0.08)', bgcolor: 'var(--color-surface)' }}>
            <Stack spacing={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">{servico?.descricao}</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {servico?.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Adicional: {profissional?.nome}</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {profissional?.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={800}>Total a pagar</Typography>
                <Typography variant="h6" fontWeight={900} color="var(--color-primary)">
                  {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Button 
            fullWidth 
            variant="contained" 
            size="large"
            onClick={handleFinalizar}
            disabled={!isValido || loading}
            startIcon={!loading && <CheckCircleOutlineIcon />}
            sx={{ 
              py: 2, borderRadius: 4, fontWeight: 800, 
              bgcolor: 'var(--color-primary)',
              transition: '0.3s',
              '&:disabled': { bgcolor: 'rgba(0,0,0,0.12)' },
              '&:hover': { bgcolor: 'var(--color-primary)', filter: 'brightness(0.9)' }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'CONFIRMAR E AGENDAR'}
          </Button>
        </Stack>
      </Box>
    </Fade>
  );
};